import * as yup from 'yup'

import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { Formik } from 'formik'
import { WhitelistedVehicleDTO } from '../../apis/whitelist/dto'
import { MoovyAction } from '../../app/permissions'
import { MoovyForm } from '../../components'
import { isValidLicensePlate } from '../../utils/validator'

export type OnSubmitCallback = (values: FormValues) => void

export type FormWhitelistedVehicleProps = {
  onSubmit: OnSubmitCallback
  onDelete: OnSubmitCallback
  formReference: string
  mode: MoovyAction
  vehicle: WhitelistedVehicleDTO | null
  formTitleKey: string
}

export interface FormValues {
  licensePlate: string
  name: string
  comment: string
  delete?: boolean
}

const FormWhitelistedVehicle = (props: FormWhitelistedVehicleProps) => {
  const intl = useIntl()
  const { vehicle } = props

  const [formikValidateOnChange, setFormikValidateOnChange] = useState(false)

  const defaultValues = {
    licensePlate: vehicle?.licensePlate ?? '',
    name: vehicle?.name ?? '',
    comment: '',
    delete: false,
  }

  const validationSchema = () => {
    return yup.object().shape({
      licensePlate: yup
        .string()
        .trim()
        .test(
          'licensePlate',
          intl.formatMessage({ id: 'common.form.errors.licensePlate' }),
          (licensePlate?: string) => isValidLicensePlate(licensePlate),
        ),
      name: yup
        .string()
        .trim()
        .required(intl.formatMessage({ id: 'common.form.errors.empty' })),
      comment: yup
        .string()
        .trim()
        .required(intl.formatMessage({ id: 'common.form.errors.empty' })),
      delete: yup.boolean(),
    })
  }

  const onSubmitFormValues = (values: FormValues) => {
    const { licensePlate, name, comment } = values
    if (values.delete) {
      props.onDelete({
        licensePlate: licensePlate.trim(),
        name: name.trim(),
        comment: comment.trim(),
      })
    } else {
      props.onSubmit({
        licensePlate: licensePlate.trim(),
        name: name.trim(),
        comment: comment.trim(),
      })
    }
  }

  return (
    <>
      <Formik
        validate={() => setFormikValidateOnChange(true)}
        validationSchema={validationSchema()}
        enableReinitialize={true}
        initialValues={defaultValues}
        validateOnChange={formikValidateOnChange}
        onSubmit={(values) => {
          onSubmitFormValues(values)
        }}
      >
        {({ values, errors, handleChange, handleSubmit }) => (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              const submitType =
                (event.nativeEvent as SubmitEvent).submitter
                  ?.getAttribute('formAction')
                  ?.toLowerCase() ?? 'submit'
              if (submitType === 'delete') {
                values.delete = true
              }
              handleSubmit()
            }}
            id={props.formReference}
          >
            <MoovyForm.FormContainer>
              <MoovyForm.FormTitle
                title={<FormattedMessage id={props.formTitleKey} />}
              />
              <MoovyForm.FormBoxRow flexDirection={'column'}>
                <MoovyForm.FormTextField
                  name="name"
                  data-testid="name"
                  value={values.name}
                  autoFocus
                  label={intl.formatMessage({
                    id: 'page.whitelist.form.label.name',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'page.whitelist.form.label.name',
                  })}
                  onChange={handleChange}
                  error={!!errors.name}
                  errorText={errors.name}
                  disabled={props.mode === 'READ'}
                />
                <MoovyForm.FormTextField
                  name="licensePlate"
                  data-testid="licensePlate"
                  value={values.licensePlate}
                  label={intl.formatMessage({
                    id: 'common.text.licensePlate',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'common.text.licensePlate',
                  })}
                  onChange={handleChange}
                  error={!!errors.licensePlate}
                  errorText={errors.licensePlate}
                  disabled={['READ', 'EDIT'].includes(props.mode)}
                />
              </MoovyForm.FormBoxRow>
              <MoovyForm.FormBoxRow>
                <MoovyForm.FormTextField
                  name="comment"
                  data-testid="comment"
                  value={values.comment}
                  label={intl.formatMessage({
                    id: 'page.whitelist.form.label.comment',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'page.whitelist.form.label.comment',
                  })}
                  onChange={handleChange}
                  error={!!errors.comment}
                  errorText={errors.comment}
                  disabled={props.mode === 'READ'}
                />
              </MoovyForm.FormBoxRow>
            </MoovyForm.FormContainer>
          </form>
        )}
      </Formik>
    </>
  )
}

export default FormWhitelistedVehicle
